import { useState, useEffect } from 'react';
import { Download, Filter, Search, Calendar, User, Shield, RefreshCw } from 'lucide-react';
import { auditService } from '../../services/auditService';
import RoleBadge from '../../components/ui/RoleBadge';
import { roleService } from '../../services/roleService';

export default function AuditTrail() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [userRoles, setUserRoles] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    actionType: [],
    performedBy: '',
    searchText: '',
    limit: 50,
    offset: 0
  });

  const [showFilters, setShowFilters] = useState(false);
  const [availableActors, setAvailableActors] = useState([]);

  // Action type options
  const actionTypes = [
    { value: 'role_created', label: 'Role Created', color: 'bg-green-100 text-green-800' },
    { value: 'role_updated', label: 'Role Updated', color: 'bg-blue-100 text-blue-800' },
    { value: 'role_deleted', label: 'Role Deleted', color: 'bg-red-100 text-red-800' },
    { value: 'role_assigned', label: 'Role Assigned', color: 'bg-purple-100 text-purple-800' },
    { value: 'role_unassigned', label: 'Role Unassigned', color: 'bg-orange-100 text-orange-800' },
    { value: 'permission_granted', label: 'Permission Granted', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'permission_revoked', label: 'Permission Revoked', color: 'bg-rose-100 text-rose-800' },
    { value: 'permission_updated', label: 'Permission Updated', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'user_created', label: 'User Created', color: 'bg-teal-100 text-teal-800' },
    { value: 'user_updated', label: 'User Updated', color: 'bg-cyan-100 text-cyan-800' },
    { value: 'settings_updated', label: 'Settings Updated', color: 'bg-amber-100 text-amber-800' },
    { value: 'bulk_permission_change', label: 'Bulk Permission Change', color: 'bg-violet-100 text-violet-800' }
  ];

  useEffect(() => {
    loadAuditLogs();
    loadAvailableActors();
    loadStatistics();
  }, []);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await auditService?.getAuditLogs(filters);
      setAuditLogs(data);
      
      // Load roles for all users in audit logs
      await loadRolesForUsers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRolesForUsers = async (logs) => {
    const uniqueUserIds = new Set();
    logs?.forEach(log => {
      if (log?.performedBy) uniqueUserIds?.add(log?.performedBy);
      if (log?.affectedUserId) uniqueUserIds?.add(log?.affectedUserId);
    });

    const rolesMap = {};
    for (const userId of uniqueUserIds) {
      try {
        const role = await roleService?.getUserPrimaryRole(userId);
        if (role) {
          rolesMap[userId] = role;
        }
      } catch (err) {
        console.error(`Error loading role for user ${userId}:`, err);
      }
    }
    setUserRoles(rolesMap);
  };

  const loadAvailableActors = async () => {
    try {
      const actors = await auditService?.getAuditActors();
      setAvailableActors(actors);
    } catch (err) {
      console.error('Error loading actors:', err);
    }
  };

  const loadStatistics = async () => {
    try {
      const endDate = new Date()?.toISOString()?.split('T')?.[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const stats = await auditService?.getAuditStatistics(startDate, endDate);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleActionTypeToggle = (actionType) => {
    setFilters(prev => {
      const currentTypes = prev?.actionType || [];
      const newTypes = currentTypes?.includes(actionType)
        ? currentTypes?.filter(t => t !== actionType)
        : [...currentTypes, actionType];
      return { ...prev, actionType: newTypes, offset: 0 };
    });
  };

  const applyFilters = () => {
    loadAuditLogs();
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      actionType: [],
      performedBy: '',
      searchText: '',
      limit: 50,
      offset: 0
    });
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const csvContent = await auditService?.exportAuditLogs(filters);
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL?.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      window.URL?.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Failed to export audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionTypeColor = (actionType) => {
    const type = actionTypes?.find(t => t?.value === actionType);
    return type?.color || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString();
  };

  const calculateActivityVolume = () => {
    if (!statistics) return { daily: 0, mostActive: null, total: auditLogs?.length };
    
    const dailyAverage = statistics?.reduce((sum, stat) => sum + parseInt(stat?.actionCount), 0) / 30;
    const actionCounts = {};
    
    statistics?.forEach(stat => {
      actionCounts[stat.actionType] = (actionCounts?.[stat?.actionType] || 0) + parseInt(stat?.actionCount);
    });
    
    const mostActive = Object.keys(actionCounts)?.reduce((a, b) => 
      actionCounts?.[a] > actionCounts?.[b] ? a : b, null
    );
    
    return {
      daily: Math.round(dailyAverage),
      mostActive,
      total: auditLogs?.length
    };
  };

  const stats = calculateActivityVolume();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
              <p className="text-gray-600 mt-1">Complete compliance tracking and security monitoring</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => loadAuditLogs()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={loading || auditLogs?.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Statistics Dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Daily Average</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.daily}</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Most Active</p>
                  <p className="text-lg font-bold text-gray-900">{stats?.mostActive || 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Total Events</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-gray-700 font-medium"
            >
              <Filter className="w-5 h-5" />
              Filters
              <span className="text-sm text-gray-500">
                ({filters?.actionType?.length} active)
              </span>
            </button>
            {showFilters && (
              <button
                onClick={resetFilters}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Reset All
              </button>
            )}
          </div>

          {showFilters && (
            <div className="space-y-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Description
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filters?.searchText}
                    onChange={(e) => handleFilterChange('searchText', e?.target?.value)}
                    placeholder="Search audit descriptions..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters?.startDate}
                    onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters?.endDate}
                    onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Action Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Action Types
                </label>
                <div className="flex flex-wrap gap-2">
                  {actionTypes?.map(type => (
                    <button
                      key={type?.value}
                      onClick={() => handleActionTypeToggle(type?.value)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        filters?.actionType?.includes(type?.value)
                          ? type?.color
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin User Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Performed By
                </label>
                <select
                  value={filters?.performedBy}
                  onChange={(e) => handleFilterChange('performedBy', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Users</option>
                  {availableActors?.map(actor => (
                    <option key={actor?.id} value={actor?.id}>
                      {actor?.firstName} {actor?.lastName} ({actor?.email})
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={applyFilters}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Audit Log Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : auditLogs?.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No audit logs found</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performed By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditLogs?.map(log => (
                    <>
                      <tr key={log?.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(log?.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getActionTypeColor(log?.actionType)}`}>
                            {log?.actionType?.replace(/_/g, ' ')?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {log?.actionDescription}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log?.performedByUser ? (
                            <div className="space-y-2">
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {log?.performedByUser?.first_name} {log?.performedByUser?.last_name}
                                </p>
                                <p className="text-xs text-gray-500">{log?.performedByUser?.email}</p>
                              </div>
                              {userRoles?.[log?.performedBy] && (
                                <RoleBadge
                                  roleName={userRoles?.[log?.performedBy]?.name}
                                  displayName={userRoles?.[log?.performedBy]?.displayName}
                                  hierarchyLevel={userRoles?.[log?.performedBy]?.hierarchyLevel}
                                  size="sm"
                                  variant="pill"
                                  showIcon={false}
                                  showHierarchy={false}
                                />
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">System</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {log?.ipAddress || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            {expandedLog === log?.id ? 'Hide' : 'Show'}
                          </button>
                        </td>
                      </tr>
                      {expandedLog === log?.id && (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 bg-gray-50">
                            <div className="space-y-3">
                              {log?.affectedUser && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 mb-2">Affected User:</p>
                                  <div className="flex items-center gap-3">
                                    <p className="text-sm text-gray-900">
                                      {log?.affectedUser?.first_name} {log?.affectedUser?.last_name} ({log?.affectedUser?.email})
                                    </p>
                                    {userRoles?.[log?.affectedUserId] && (
                                      <RoleBadge
                                        roleName={userRoles?.[log?.affectedUserId]?.name}
                                        displayName={userRoles?.[log?.affectedUserId]?.displayName}
                                        hierarchyLevel={userRoles?.[log?.affectedUserId]?.hierarchyLevel}
                                        size="sm"
                                        variant="pill"
                                        showIcon={true}
                                        showHierarchy={true}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                              {log?.affectedRole && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 mb-2">Affected Role:</p>
                                  <RoleBadge
                                    roleName={log?.affectedRole?.name}
                                    displayName={log?.affectedRole?.display_name}
                                    hierarchyLevel={log?.affectedRole?.hierarchy_level}
                                    size="md"
                                    variant="badge"
                                    showIcon={true}
                                    showHierarchy={true}
                                  />
                                </div>
                              )}
                              {log?.beforeState && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700">Before State:</p>
                                  <pre className="text-xs text-gray-900 bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                                    {JSON.stringify(log?.beforeState, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log?.afterState && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700">After State:</p>
                                  <pre className="text-xs text-gray-900 bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                                    {JSON.stringify(log?.afterState, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log?.userAgent && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700">User Agent:</p>
                                  <p className="text-xs text-gray-600">{log?.userAgent}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}